// Brand faces, self-hosted via @fontsource — no external font host. These must
// be imported from JS, not from index.css: Tailwind v4 flattens CSS @imports
// before Vite can rewrite the `url()` refs, so the woff2 files never reach
// dist/. See the note at the top of index.css.
import '@fontsource-variable/fraunces'
import '@fontsource/space-grotesk/400.css'
import '@fontsource/space-grotesk/500.css'
import '@fontsource/space-grotesk/700.css'
import '@fontsource/ibm-plex-mono/400.css'
import '@fontsource/ibm-plex-mono/500.css'
import './index.css'
import App from './App.tsx'
import { mountClient } from '@gjl/site-kit'
import { initAppSentry } from '@gjl/sentry/app'
import { initOutboundClickTracking } from './lib/analytics'
import { captureFirstTouch } from './lib/attribution'

/**
 * GA4 — property 527738893, the same property the hymnal build reports into, so
 * a visitor's path from a hymn page through to a marketing page stays one funnel.
 *
 * This REPLACES the inline gtag snippet that used to sit in index.html. That
 * snippet configured GA4 without `send_page_view: false` and never called
 * `initGA4`, so `analyticsEnabled()` was false and the `<PageviewTracker/>` that
 * `mountClient` already renders no-opped. The site therefore reported exactly
 * ONE page_view per hard load and none for client-side navigation — every
 * interior route's traffic was invisible, and the page reports were really
 * landing-page reports.
 *
 * `createGoogleAnalyticsProvider` sets `send_page_view: false` and the tracker
 * supplies every view, so there is no double count. Deleting the snippet rather
 * than patching it is what makes that structural: the provider unconditionally
 * re-issues `config` and appends its own gtag/js tag, so keeping both would
 * have meant two configs for one measurement id.
 *
 * The id goes to `mountClient`, which already owns this bootstrap for CMS-published
 * sites — this build simply has no `__CMS_DATA__` to read one from.
 *
 * THE ID IS BUILD CONFIG, NOT A LITERAL (2026-09-05). This used to fall back to
 * a hardcoded `G-0WNEJ3Q8E4` whenever `import.meta.env.PROD` was true — but PROD
 * is true for EVERY production-mode build, including the dev-environment deploy
 * and the prerender CI runs on a PR. So dev.fuguelab.com and CI both reported
 * into the production property: 19 and 21 sessions respectively over 60 days,
 * mixed into 1,139 real ones.
 *
 * Now the id comes only from `VITE_GA_ID`, which the buildspec exports for the
 * prod environment alone. A dev build has no id and `initGA4` no-ops.
 *
 * THE RISK THIS CREATES, AND WHERE IT IS HANDLED: a missing var no longer falls
 * back to something that works, it falls back to silence — the exact shape of
 * the recipes VITE_API_URL incident. The prod buildspec asserts the var is set
 * before building, so prod fails loudly rather than going dark quietly.
 */
const GA_ID = import.meta.env.VITE_GA_ID as string | undefined

// Client-only: attaches a document click listener. main.tsx is the browser
// entry (entry-server.tsx never imports it), but guard anyway for safety.
if (typeof window !== 'undefined') {
  initOutboundClickTracking()

  // How this visitor found us, for the app signup links (lib/attribution.ts).
  captureFirstTouch()

  // Error reporting, set up BEFORE mountClient so a crash during hydration is
  // caught. This site is prerendered, so a React crash still serves HTTP 200
  // with real HTML and the rate(5 minutes) site monitor reports UP — there is no
  // server in the page path to log anything either. That combination is why a
  // white screen here went unnoticed until someone loaded the page.
  // See docs/pm/active/frontend-error-reporting.md §1.
  initAppSentry({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    release: import.meta.env.VITE_APP_VERSION,
    tags: { site: 'studio' },
  })
}

// Prerendered HTML -> hydrate; empty root (dev) -> fresh render. mountClient
// wraps StrictMode + BrowserRouter and picks hydrate vs render automatically,
// and renders the <PageviewTracker/> that reports client-side navigation.
mountClient(App, { ga4MeasurementId: GA_ID })
